import { useSyncExternalStore } from "react";
import i18n from "./i18n";
import {
  SUPPORTED_LANGUAGES,
  isAppLanguageCode,
  type AppLanguageCode,
  applyDocumentLanguage,
  LOCALE_STORAGE_KEY,
} from "./i18n/languages";

/** Stable default — useSyncExternalStore requires referentially equal snapshots when unchanged. */
const ALL_LANGUAGE_CODES: AppLanguageCode[] = SUPPORTED_LANGUAGES.map((l) => l.code);

/** null = not loaded yet (treat as all enabled until first fetch). */
let enabledSnapshot: AppLanguageCode[] | null = null;
let fetchStarted = false;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function normalizeEnabled(input: unknown): AppLanguageCode[] {
  if (!Array.isArray(input)) return ALL_LANGUAGE_CODES;
  const wanted = new Set(
    input
      .map((x) => String(x ?? "").trim().toLowerCase())
      .filter((c): c is AppLanguageCode => isAppLanguageCode(c))
  );
  // Same codes as default → reuse the stable constant (avoids a new array identity).
  const next = ALL_LANGUAGE_CODES.filter((c) => wanted.has(c));
  if (next.length === ALL_LANGUAGE_CODES.length) return ALL_LANGUAGE_CODES;
  return next;
}

/** Effective list for selectors: empty means hide selectors and force English. */
export function getEnabledLanguages(): AppLanguageCode[] {
  if (enabledSnapshot == null) return ALL_LANGUAGE_CODES;
  return enabledSnapshot;
}

export function languageSelectorsVisible(): boolean {
  return getEnabledLanguages().length > 0;
}

/**
 * Resolve which language to use given the allowlist.
 * - All off → English
 * - Current not enabled → English if enabled, else first enabled
 */
export function resolveLanguageAgainstAllowlist(preferred: string | null | undefined): AppLanguageCode {
  const enabled = getEnabledLanguages();
  if (enabled.length === 0) return "en";
  const code = String(preferred ?? "")
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];
  if (code && isAppLanguageCode(code) && enabled.includes(code)) return code;
  if (enabled.includes("en")) return "en";
  return enabled[0] ?? "en";
}

export function applyEnabledLanguagesPolicy(preferred?: string | null): AppLanguageCode {
  const current = String(preferred ?? i18n.language ?? "")
    .trim()
    .toLowerCase()
    .split(/[-_]/)[0];
  const next = resolveLanguageAgainstAllowlist(preferred ?? i18n.language);
  applyDocumentLanguage(next);
  // Compare normalized codes so `en-GB` vs `en` does not thrash changeLanguage.
  if (current !== next) {
    void i18n.changeLanguage(next);
  }
  try {
    if (enabledSnapshot && enabledSnapshot.length === 0) {
      // All off: keep English in storage so selectors stay forced after reload.
      window.localStorage.setItem(LOCALE_STORAGE_KEY, "en");
    } else if (isAppLanguageCode(next)) {
      const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
      if (stored && isAppLanguageCode(stored) && !getEnabledLanguages().includes(stored)) {
        window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
      }
    }
  } catch {
    // Ignore storage errors.
  }
  return next;
}

export function notifyEnabledLanguagesChanged(enabled: string[]) {
  enabledSnapshot = normalizeEnabled(enabled);
  applyEnabledLanguagesPolicy();
  emit();
}

async function fetchEnabledLanguages(): Promise<void> {
  try {
    const res = await fetch("/api/languages");
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      enabledSnapshot = ALL_LANGUAGE_CODES;
    } else {
      enabledSnapshot = normalizeEnabled((data as { enabled?: unknown }).enabled);
    }
  } catch {
    enabledSnapshot = ALL_LANGUAGE_CODES;
  }
  applyEnabledLanguagesPolicy();
  emit();
}

export function ensureEnabledLanguagesLoaded(): void {
  if (fetchStarted) return;
  fetchStarted = true;
  void fetchEnabledLanguages();
}

function subscribe(onStoreChange: () => void) {
  listeners.add(onStoreChange);
  ensureEnabledLanguagesLoaded();
  return () => {
    listeners.delete(onStoreChange);
  };
}

function getSnapshot(): AppLanguageCode[] {
  return getEnabledLanguages();
}

function getServerSnapshot(): AppLanguageCode[] {
  return ALL_LANGUAGE_CODES;
}

/** Subscribe to platform language allowlist (public /api/languages). */
export function useEnabledLanguages(): {
  enabled: AppLanguageCode[];
  selectorsVisible: boolean;
  loaded: boolean;
} {
  const enabled = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return {
    enabled,
    selectorsVisible: enabled.length > 0,
    loaded: enabledSnapshot != null,
  };
}
